import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetActiveBankAccountsQuery } from 'store/api/companies';
import { CompanyBankAccountsGroupedDto } from 'types/companies';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { ContactForm, PartyType } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { useClassifiers } from 'utils/useClassifiers';
import { CONTACT_FORM_CLASSIFIERS, contactDtoToForm } from './contactFormMapping';
import ContactPageForm from './ContactPageForm';

// Referentially stable so the destructuring default below does not build a new array on every
// render while the query is unresolved — that identity churn re-fires the initial-data effect,
// which sets fresh state, which renders again.
const NO_BANK_ACCOUNTS: CompanyBankAccountsGroupedDto[] = [];

const ClassicContactPageEdit = () => {
    const { id: contactId } = useParams();
    const [initialData, setInitialData] = useState<ContactForm>();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: CONTACT_FORM_CLASSIFIERS,
    });

    const { data: contact, isError, isLoading, refetch } = useGetContactByIdQuery(contactId ?? skipToken);

    const { data: bankAccountsGrouped = NO_BANK_ACCOUNTS } = useGetActiveBankAccountsQuery();

    const entityType = useMemo(() => {
        if (contact?.partyType === undefined) {
            return null;
        }
        return contact.partyType === PartyType.COMPANY ? EntityType.PARTY_COMPANY : EntityType.PARTY_PERSON;
    }, [contact?.partyType]);

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType, fields: contact?.customFields as CustomField[] });

    useEffect(() => {
        if (isLoadingCustomFieldData) {
            return;
        }
        setInitialData(
            contact
                ? contactDtoToForm(contact, {
                      classifiers,
                      bankAccountsGrouped,
                      customFieldValues: initialCustomFieldsObject,
                  })
                : undefined
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact, classifiers, isLoadingCustomFieldData, bankAccountsGrouped]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return (
        <ContactPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            isDirectlyConnectedToMatterAsClient={contact?.isDirectlyConnectedToMatterAsClient}
            contact={contact}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={customFields}
        />
    );
};

/** In the new design a contact is edited section by section on its page, so its edit URL opens About there. */
const ContactPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: contactId } = useParams();
    return isNew ? <Navigate to={`/contact/${contactId}?edit=about`} replace /> : <ClassicContactPageEdit />;
};

export default ContactPageEdit;
